import { CloseIcon, SearchIcon } from '@sketchnote/icons';
import { BillableIcon, CalenderIcon, TimeHourglassIcon } from '@sketchnote/icons';
import { Switch } from '@sketchnote/shared-components';
import cn from 'classnames';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import { useMemo, useState } from 'react';
// import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { TimeLogDetails } from 'src/features/tasks/components/TimeTrackerDetails';
import { getFormattedValue } from 'src/utils/getFormattedValue';

import { DateRangePicker } from '../../tasks/components/DatePicker';
import { GlobalTaskData, useTimesheetStore } from '../store/useTimesheetStore';

export interface TaskOption {
  readonly value: string;
  readonly label: string;
  readonly data: GlobalTaskData;
}

const StartDateSelector = ({
  onSetStartDate,
  startDate,
}: {
  onSetStartDate: (dates: any) => void;
  startDate;
}) => {
  return (
    <div className="mb-16">
      <label htmlFor="assignDate" className="text-sm mb-8 block text-gray-700 font-medium">
        Select date
      </label>
      <div className="relative">
        <DateRangePicker
          value={startDate?.length ? startDate.map((date) => parseISO(date)) : [new Date()]}
          onChange={onSetStartDate}
          placeholderText="Select date"
          initialTab="date"
          show={['date']}
          disableEndDate={true}
          inputClassName="datepicker bg-gray-100 p-12 pl-50 w-full rounded-sm h-48 border border-gray-200 text-left text-gray-400"
        />
        <CalenderIcon
          wrapperClass="text-gray-400 absolute p-5 top-8 left-10 left-12 text-gray-400"
          size={30}
        />
      </div>
    </div>
  );
};

function TimeLogIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" viewBox="0 0 40 40">
      <rect
        width="32"
        height="32"
        fill="#254DDA"
        rx="6"
        transform="matrix(.97745 .21116 -.20471 .97882 7.582 0)"
      ></rect>
      <g filter="url(#filter0_b_4911_51433)">
        <rect width="32" height="32" y="6" fill="#fff" fillOpacity="0.5" rx="6"></rect>
      </g>
      <path
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M14.742 13.858l-4.159 1.559c-.958.358-1.741 1.491-1.741 2.516v6.192c0 .983.65 2.275 1.441 2.867l3.584 2.675c1.175.883 3.108.883 4.283 0l3.584-2.675c.791-.592 1.441-1.884 1.441-2.867v-6.192c0-1.025-.783-2.158-1.742-2.516l-4.158-1.559c-.708-.258-1.841-.258-2.533 0z"
      ></path>
      <path
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        strokeWidth="1.5"
        d="M16 24.917a3.333 3.333 0 100-6.667 3.333 3.333 0 000 6.667z"
      ></path>
      <path
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        strokeWidth="1.5"
        d="M16.209 20.542v.775c0 .291-.15.566-.409.716l-.633.384"
      ></path>
      <defs>
        <filter
          id="filter0_b_4911_51433"
          width="44"
          height="44"
          x="-6"
          y="0"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
          <feGaussianBlur in="BackgroundImageFix" stdDeviation="3"></feGaussianBlur>
          <feComposite
            in2="SourceAlpha"
            operator="in"
            result="effect1_backgroundBlur_4911_51433"
          ></feComposite>
          <feBlend
            in="SourceGraphic"
            in2="effect1_backgroundBlur_4911_51433"
            result="shape"
          ></feBlend>
        </filter>
      </defs>
    </svg>
  );
}

export const AddTimeLog = ({ onClose }: { onClose: () => void }) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskData, setSelectedTaskData] = useState<{
    data: GlobalTaskData;
    label: string;
  } | null>(null);
  const [hours, setHours] = useState<number | undefined>();
  const [minutes, setMinutes] = useState<number | undefined>();
  const [startDate, setStartDate] = useState();
  const [isBillable, setIsBillable] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const { globalTimelogs, addTimeLogEntry } = useTimesheetStore();

  const options: TaskOption[] = useMemo(() => {
    return globalTimelogs.map((log) => ({
      label: getFormattedValue(log?.name) || 'Untitled',
      value: log.id,
      data: log,
    }));
  }, [globalTimelogs]);

  const promiseOptions = (inputValue: string) =>
    new Promise<TaskOption[]>((resolve) => {
      setTimeout(() => {
        resolve(options.filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase())));
      }, 500);
    });

  const onSelectTask = (selectedOption: TaskOption) => {
    if (!selectedOption) {
      setSelectedTaskId(null);
      setSelectedTaskData(null);
      return;
    }
    const { value, data, label } = selectedOption;
    if (value) {
      setSelectedTaskId(value);
      setSelectedTaskData({ label, data });
    }
  };

  const handleHoursInput = (e) => {
    if (e.target.value < 10000) setHours(e.target.value);
  };

  const handleMinutesInput = (e) => {
    if (e.target.value < 60) setMinutes(e.target.value);
  };

  const onSetStartDate = (dates) => {
    const dateValues = dates.length ? dates?.map((date) => formatISO(date)) : [formatISO(dates)];
    setStartDate(dateValues);
  };

  const onSubmitTimeLog = async () => {
    if (!selectedTaskId) return;
    if (!hours && !minutes) return;

    const payload = {
      taskId: selectedTaskId,
      billable: isBillable,
      description,
      duration: { hours: hours?.toString() || '0', minutes: minutes?.toString() || '0' },
      startDate: startDate || [formatISO(new Date())],
    };

    await addTimeLogEntry(payload);
    onClose();
  };

  return (
    <div className="h-screen flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between px-20 pt-20">
          <TimeLogIcon />
          <button
            className=" w-32 h-32 flex items-center justify-center bg-gray-100 rounded-sm"
            onClick={onClose}
          >
            <CloseIcon size={12} className="text-gray-700" />
          </button>
        </div>
        <h2 className="text-xl px-20 font-medium mt-8 border-b">Time log</h2>
        <div className="px-20 pt-20">
          <AsyncSelect
            styles={{
              control: (provided) => ({
                ...provided,
                background: '#F6F7F9',
                padding: '4px',
                borderColor: '#E2E4E9',
              }),
            }}
            cacheOptions
            noOptionsMessage={({ inputValue }) => {
              if (inputValue.trim() === '') {
                return null;
              }

              return 'No options found';
            }}
            isClearable
            onChange={onSelectTask}
            loadOptions={promiseOptions}
            placeholder="Search tasks"
            components={{
              IndicatorSeparator: () => null,
              DropdownIndicator: () => <SearchIcon className=" text-gray-400 mr-10" />,
            }}
          />
        </div>
        <div className="p-20">
          <div className=" bg-gray-100 text-gray-700 w-full py-12 px-12 rounded-sm mb-16">
            {selectedTaskId && selectedTaskData ? (
              <>
                <p className="text-sm font-bold">{selectedTaskData?.label}</p>
                <p className="text-xs font-regular">
                  {`Workspace: ${selectedTaskData?.data?.workspace?.name || ''}`}
                </p>
                <p className="text-xs font-regular">{`File: ${
                  selectedTaskData?.data?.file?.title || 'Untitled'
                }`}</p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold mb-4 opacity-50">Task</p>
                <p className="text-xs font-regular opacity-50">[Task not selected]</p>
              </>
            )}
          </div>
          {selectedTaskId && (
            <section>
              <label htmlFor="assignTime" className="text-sm mb-8 block text-gray-700 font-medium">
                Add time
              </label>
              <div className="flex justify-between mb-16">
                <div className="relative w-full mr-8">
                  <input
                    id="totalEstimatedTime"
                    type="number"
                    min="0"
                    max="10000"
                    className=" bg-gray-100 p-12 pl-50 w-full placeholder-gray-400 rounded-sm h-48 border border-gray-200 text-left no-input-arrow"
                    placeholder="Hours"
                    autoComplete="off"
                    onChange={handleHoursInput}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    value={hours}
                  />
                  <TimeHourglassIcon
                    wrapperClass="text-gray-400 absolute p-5 top-12 left-10 left-12 border-2 border-gray-400 text-gray-400 border-dotted rounded-full"
                    size={12}
                  />
                  <span className="absolute right-14 top-16 text-xs text-gray-500">hours</span>
                </div>
                <div className="relative w-full">
                  <input
                    id="totalEstimatedTime"
                    type="number"
                    min="0"
                    max="60"
                    className=" bg-gray-100 p-12 pl-50 w-full placeholder-gray-400 rounded-sm h-48 border border-gray-200 text-left no-input-arrow"
                    placeholder="Mins"
                    autoComplete="off"
                    onChange={handleMinutesInput}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    value={minutes}
                  />
                  <TimeHourglassIcon
                    wrapperClass="text-gray-400 absolute p-5 top-12 left-10 left-12 border-2 border-gray-400 text-gray-400 border-dotted rounded-full"
                    size={12}
                  />
                  <span className="absolute right-14 top-16 text-xs text-gray-500">mins</span>
                </div>
              </div>
              <StartDateSelector onSetStartDate={onSetStartDate} startDate={startDate} />
              <TimeLogDetails
                isBillable={isBillable}
                setIsBillable={setIsBillable}
                description={description}
                setDescription={setDescription}
              />
            </section>
          )}
        </div>
      </div>
      <footer className="bg-gray-100 flex p-10 border-t w-full">
        <button
          className={cn('flex p-6 mr-12 justify-center items-center text-white rounded', {
            'bg-primary-500': selectedTaskId && ((hours && hours > 0) || (minutes && minutes > 0)),
            'bg-gray-300': !(selectedTaskId && ((hours && hours > 0) || (minutes && minutes > 0))),
          })}
          disabled={!(selectedTaskId && ((hours && hours > 0) || (minutes && minutes > 0)))}
          onClick={onSubmitTimeLog}
        >
          <span className="mx-8 font-sans font-medium text-xs">Add time log</span>
        </button>
        <button
          className="flex p-6 justify-center items-center text-gray-600 bg-white rounded border border-gray-300"
          onClick={onClose}
        >
          <span className="mx-8 font-sans font-medium text-xs">Cancel</span>
        </button>
      </footer>
    </div>
  );
};
